<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="app-container">
    <!-- Header Section with Logo, Dashboard, Profile, and Logout -->
    <Header />

    <!-- Sidebar and Main Content -->
    <main :class="mainContentClass">
      <div class="main-header">
        <h1 class="main-title">Flight Credits</h1>
        <h2 class="header-title">
          Credit Balance:
          <span class="credit-balance">{{
            creditBalance.toLocaleString()
          }}</span>
        </h2>
      </div>

      <Divider />
      <br />

      <!-- Updated layout structure -->
      <form @submit.prevent="handleCreditPurchase" class="credits-container">
        <!-- Left column -->
        <div class="credits-content">
          <div class="section-header">
            <h2 class="header-title">Top Up Credits</h2>
            <p class="section-description">
              Credits can be used for booking any flight.
            </p>
          </div>

          <div class="pricing-plans">
            <div
              v-for="plan in pricingPlans"
              :key="plan.id"
              class="plan-card"
              :class="{ selected: selectedPlan === plan.id }"
              @click="selectPlan(plan.id)"
            >
              <div class="save-badge" v-if="plan.savePercentage">
                SAVE {{ plan.savePercentage }}%
              </div>
              <div v-if="plan.id === 0" class="credit-input-container">
                <input
                  type="number"
                  id="credit"
                  class="credit-input"
                  v-model.number="credit"
                  min="1"
                  @input="validateCredit"
                />
                <div class="credit-controls">
                  <button
                    type="button"
                    class="credit-btn"
                    @click="decrementCredit"
                  >
                    <vue-feather type="minus" size="16" style="color: black" />
                  </button>
                  <button
                    type="button"
                    class="credit-btn"
                    @click="incrementCredit"
                  >
                    <vue-feather type="plus" size="16" style="color: black" />
                  </button>
                </div>
              </div>
              <h2 v-else class="price">{{ plan.flightCredits }}</h2>
              <p class="subtitle">ticket credit</p>
              <p class="per-flight">${{ plan.perFlight }} per credit</p>
            </div>
          </div>

          <div class="info-section">
            <h3>Important Information</h3>
            <ul class="info-list">
              <li>1 credit = 1 ticket</li>
              <li>Credits are valid for 12 months from the date of purchase</li>
              <li>Credits are non-refundable</li>
            </ul>
          </div>
        </div>

        <!-- Right column -->
        <div class="payment-sidebar">
          <div class="price-breakdown">
            <h2 class="breakdown-title">Purchase Details</h2>
            <div class="breakdown-row">
              <span v-if="selectedPlanData">
                ${{ selectedPlanData.perFlight }} ×
                {{ selectedPlanData.flightCredits }} credits
              </span>
              <span v-else>${{ pricePerCredit }} × {{ credit }} credits</span>
              <span>${{ totalPrice.toLocaleString() }}</span>
            </div>
            <div class="breakdown-total">
              <span>Total</span>
              <span>${{ totalPrice.toLocaleString() }}</span>
            </div>

            <div class="payment-info">
              <p class="payment-note">Secure payment processing via Stripe.</p>
            </div>

            <div class="payment-options">
              <button
                type="submit"
                class="payment-btn stripe-btn"
                :disabled="selectedPlan === null"
                :class="{
                  'disabled-btn': selectedPlan === null,
                }"
              >
                Pay with Stripe
              </button>
            </div>
          </div>
        </div>
      </form>

      <br />
      <Divider />

      <div class="table-container">
        <h2 class="header-title">Credit History</h2>
        <div v-if="isLoading" class="loading-state">
          Loading credit history...
        </div>
        <div v-else-if="creditHistory.length === 0" class="empty-state">
          <vue-feather type="credit-card" size="48" style="color: #aaa" />
          <br />
          <p class="empty-message">You don't have any credit history yet.</p>
        </div>
        <div v-else class="credit-table">
          <div class="table-header">
            <div class="th">Date</div>
            <div class="th">Reference</div>
            <div class="th">Type</div>
            <div class="th">Amount</div>
            <div class="th">Status</div>
            <div class="th">Description</div>
          </div>
          <div class="table-body">
            <div
              v-for="transaction in creditHistory"
              :key="transaction.id"
              class="table-row"
            >
              <div class="td">{{ formatDate(transaction.createdAt) }}</div>
              <div class="td">{{ transaction.referenceId }}</div>
              <div class="td">
                <span :class="['badge', transaction.category]">
                  {{
                    transaction.type.charAt(0).toUpperCase() +
                    transaction.type.slice(1)
                  }}
                </span>
              </div>
              <div class="td">{{ transaction.amount }} credits</div>
              <div class="td">
                <span :class="['status-badge', transaction.status]">
                  {{ transaction.status.toUpperCase() }}
                </span>
              </div>
              <div class="td">
                {{
                  transaction.type === "usage"
                    ? `${transaction?.description}`
                    : transaction.type === "refund"
                    ? `Refund for ${transaction?.originalTransaction?.referenceId}`
                    : transaction.planDetails
                    ? `${transaction.planDetails.planName}`
                    : `${transaction.amount} credits purchased`
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Payment Success Modal -->
    <PaymentSuccessModal
      v-if="showPaymentSuccessModal"
      :credits="successModalData.credits"
      :referenceId="successModalData.referenceId"
      @close="closeSuccessModal"
    />

    <!-- Payment Failure Modal -->
    <PaymentFailureModal
      v-if="showPaymentFailureModal"
      :referenceId="failureModalData.referenceId"
      @close="closeFailureModal"
      @retry="retryPayment"
    />

    <!-- Payment Loading Modal -->
    <PaymentLoadingModal
      v-if="showPaymentLoadingModal"
      :referenceId="loadingModalData.referenceId"
      @close="closeLoadingModal"
    />
  </div>
</template>

<script>
import { db } from "@/plugins/firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { useSessionStore } from "@/stores/session";
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Divider from "@/components/organisms/Divider.vue";
import PaymentSuccessModal from "@/components/organisms/PaymentSuccessModal.vue";
import PaymentFailureModal from "@/components/organisms/PaymentFailureModal.vue";
import PaymentLoadingModal from "@/components/organisms/PaymentLoadingModal.vue";
import { creditService } from "@/services/creditService";
import { stripeProductService } from "@/services/stripeProductService";

export default {
  data() {
    return {
      selectedPlan: 0,
      credit: 1,
      creditHistory: [],
      pricingPlans: [],
      isLoading: true,
      isLoadingPricing: true,
      // New state for payment modals
      showPaymentSuccessModal: false,
      showPaymentFailureModal: false,
      successModalData: {
        credits: 0,
        referenceId: "",
      },
      failureModalData: {
        referenceId: "",
        creditId: "",
      },
      pendingCreditId: null,
      // Loading state for payment processing
      showPaymentLoadingModal: false,
      loadingModalData: {
        referenceId: "",
        creditId: "",
      },
      paymentStatusListener: null,
    };
  },
  components: {
    Header,
    Divider,
    PaymentSuccessModal,
    PaymentFailureModal,
    PaymentLoadingModal,
  },
  computed: {
    selectedPlanData() {
      if (!this.selectedPlan) return null;
      return this.pricingPlans.find((plan) => plan.id === this.selectedPlan);
    },
    totalPrice() {
      if (this.selectedPlanData) {
        return this.selectedPlanData.price;
      } else {
        // Custom credit pricing using fetched price
        return this.credit * this.pricePerCredit;
      }
    },
    pricePerCredit() {
      if (this.selectedPlanData) {
        return this.selectedPlanData.perFlight;
      } else {
        return this.pricingPlans[0]?.perFlight || 0;
      }
    },
    creditBalance() {
      return creditService.calculateCreditBalance(this.creditHistory);
    },
  },
  methods: {
    selectPlan(planId) {
      console.log("planId", planId);
      this.selectedPlan = planId;
    },
    validateCredit() {
      // Update selectedCredit when custom credit input changes
      if (this.selectedPlan === 0) {
        // Ensure credit value doesn't exceed 100
        this.credit = Math.min(Math.max(1, this.credit), 100);
      }
    },
    incrementCredit() {
      if (this.credit < 100) {
        this.credit += 1;
      }
    },
    decrementCredit() {
      if (this.credit > 1) {
        this.credit -= 1;
      }
    },
    async handleCreditPurchase() {
      try {
        const sessionStore = useSessionStore();
        const userId = sessionStore.userId;

        console.log("this.selectedPlanData", this.selectedPlanData);

        // Get productId from user or environment
        const productId =
          sessionStore.user?.productId || process.env.VUE_APP_STRIPE_PRODUCT_ID;

        // Call the creditService to handle the purchase with Stripe
        await creditService.handleCreditPurchase(
          userId,
          this.selectedPlan,
          this.selectedPlanData,
          this.credit,
          this.pricePerCredit,
          this.totalPrice,
          productId
        );

        // The redirect to Stripe happens in the creditService
      } catch (error) {
        console.error("Error in credit purchase:", error);
        // Show error message to user
      }
    },
    async fetchPricingData() {
      try {
        this.isLoadingPricing = true;
        const sessionStore = useSessionStore();

        if (!sessionStore.user && sessionStore.userId) {
          await sessionStore.fetchUserDetails(sessionStore.userId);
        }

        // Get productId from user or environment
        const productId =
          sessionStore.user?.productId || process.env.VUE_APP_STRIPE_PRODUCT_ID;

        // Fetch pricing data from Stripe
        const pricingData = await stripeProductService.fetchProductDetails(
          productId
        );

        // Update pricing plans
        this.pricingPlans = [pricingData];
      } catch (error) {
        console.error("Error fetching pricing data:", error);
      } finally {
        this.isLoadingPricing = false;
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return "";
      return new Date(timestamp.seconds * 1000).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    // New methods for handling modals
    async checkUrlParams() {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get("status");
      const reference = urlParams.get("reference");
      const creditId = urlParams.get("creditId");

      // Clear URL parameters without reloading
      if (status && reference) {
        window.history.replaceState({}, document.title, "/credits");

        if (status === "success") {
          await this.handlePaymentSuccess(reference, creditId);
        } else if (status === "cancelled") {
          await this.handlePaymentCancelled(reference, creditId);
        }
      }
    },

    async handlePaymentSuccess(referenceId, creditId) {
      try {
        // Check if the credit status is already completed (webhook updated it)
        if (creditId) {
          const creditRef = doc(db, "credits", creditId);
          const creditDoc = await getDoc(creditRef);

          if (creditDoc.exists()) {
            const creditData = creditDoc.data();

            // Only show success modal if status is completed (webhook verified payment)
            if (creditData.status === "completed") {
              this.successModalData = {
                credits: creditData.amount,
                referenceId: creditData.referenceId,
              };
              this.showPaymentSuccessModal = true;
            } else if (creditData.status === "pending") {
              // If status is still "pending", start monitoring for webhook processing
              this.startPaymentStatusMonitoring(referenceId, creditId);
            }
          }
        }
      } catch (error) {
        console.error("Error handling payment success:", error);
      }
    },

    async handlePaymentCancelled(referenceId, creditId) {
      try {
        // Update the credit status to cancelled
        if (creditId) {
          const creditRef = doc(db, "credits", creditId);
          const creditDoc = await getDoc(creditRef);

          if (creditDoc.exists()) {
            // Update status to cancelled
            await updateDoc(creditRef, {
              status: "cancelled",
              updatedAt: new Date(),
            });

            // Show failure modal
            this.failureModalData = {
              referenceId: referenceId,
              creditId: creditId,
            };
            this.showPaymentFailureModal = true;
          }
        }
      } catch (error) {
        console.error("Error handling payment cancellation:", error);
      }
    },

    closeSuccessModal() {
      this.showPaymentSuccessModal = false;
      this.successModalData = { credits: 0, referenceId: "" };
    },

    closeFailureModal() {
      this.showPaymentFailureModal = false;
      this.failureModalData = { referenceId: "", creditId: "" };
    },

    retryPayment() {
      // Close the failure modal
      this.closeFailureModal();
      // Trigger handleCreditPurchase again
      this.handleCreditPurchase();
    },

    startPaymentStatusMonitoring(referenceId, creditId) {
      // Show loading modal
      this.loadingModalData = {
        referenceId: referenceId,
        creditId: creditId,
      };
      this.showPaymentLoadingModal = true;

      // Set up real-time listener for this specific credit document
      const creditRef = doc(db, "credits", creditId);

      this.paymentStatusListener = onSnapshot(
        creditRef,
        (doc) => {
          if (doc.exists()) {
            const creditData = doc.data();

            if (creditData.status === "completed") {
              // Payment verified by webhook - show success modal
              this.closeLoadingModal();
              this.successModalData = {
                credits: creditData.amount,
                referenceId: creditData.referenceId,
              };
              this.showPaymentSuccessModal = true;
            } else if (
              creditData.status === "failed" ||
              creditData.status === "cancelled"
            ) {
              // Payment failed - show failure modal
              this.closeLoadingModal();
              this.failureModalData = {
                referenceId: referenceId,
                creditId: creditId,
              };
              this.showPaymentFailureModal = true;
            }
          }
        },
        (error) => {
          console.error("Error monitoring payment status:", error);
          this.closeLoadingModal();
        }
      );

      // Set timeout to stop monitoring after 3 minutes
      setTimeout(() => {
        if (this.showPaymentLoadingModal) {
          this.handlePaymentTimeout();
        }
      }, 60000); // 1 minutes
    },

    closeLoadingModal() {
      this.showPaymentLoadingModal = false;
      this.loadingModalData = { referenceId: "", creditId: "" };

      // Clean up listener
      if (this.paymentStatusListener) {
        this.paymentStatusListener();
        this.paymentStatusListener = null;
      }
    },

    handlePaymentTimeout() {
      this.closeLoadingModal();
      // Show failure modal with timeout message
      this.failureModalData = {
        referenceId: this.loadingModalData.referenceId,
        creditId: this.loadingModalData.creditId,
        isTimeout: true,
      };
      this.showPaymentFailureModal = true;
    },
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-main-content" : "web-main-content";
    });

    return {
      mainContentClass,
    };
  },
  async created() {
    const sessionStore = useSessionStore();
    const userId = sessionStore.userId;

    // Fetch pricing data
    await this.fetchPricingData();

    // Set up real-time listener for credit transactions
    const q = query(
      collection(db, "credits"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );

    this.unsubscribe = onSnapshot(q, (snapshot) => {
      this.creditHistory = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      this.isLoading = false;
    });
  },

  async mounted() {
    // Check URL parameters for payment status
    this.checkUrlParams();
  },

  beforeUnmount() {
    // Clean up the listeners when component is destroyed
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    if (this.paymentStatusListener) {
      this.paymentStatusListener();
    }
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
}

/* Updated layout styles */
.credits-container {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 48px;
  margin-bottom: 48px;
}

.credits-content {
  min-width: 0;
}

.section-header {
  margin-bottom: 32px;
}

.section-description {
  color: #6b7280;
  font-size: 16px;
  line-height: 1.5;
  margin-top: 8px;
}

.info-section {
  margin-top: 48px;
  padding: 24px;
  background-color: #f9fafb;
  border-radius: 12px;
}

.info-section h3 {
  font-size: 18px;
  margin-bottom: 16px;
  color: #111827;
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.info-list li {
  position: relative;
  padding-left: 24px;
  margin-bottom: 12px;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.5;
}

.info-list li:before {
  content: "•";
  position: absolute;
  left: 8px;
  color: #9ca3af;
}

.payment-sidebar {
  position: sticky;
  top: 24px;
  align-self: start;
}

.payment-info {
  margin: 24px 0;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.payment-note {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}

.credit-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.credit-input {
  border: none;
  font-size: 42px;
  padding: 0px;
  padding-right: 70px;
  width: 100%;
  outline: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.credit-controls {
  position: absolute;
  right: 0;
  display: flex;
  flex-direction: column-reverse;
  gap: 8px;
  margin-top: 20px;
}

.credit-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.credit-btn:hover {
  background-color: #e5e7eb;
}

.credit-btn:active {
  background-color: #d1d5db;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .credits-container {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .payment-sidebar {
    position: static;
  }

  .price-breakdown {
    max-width: none;
  }
}

/* Main Content */
.mobile-main-content {
  margin-top: 20px;
  padding: 20px;
}

.web-main-content {
  padding: 8rem 10rem;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
  display: none;
}

.main-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.main-title {
  font-size: 3rem;
  color: #000;
}

.form-group {
  margin-bottom: 1.2rem;
  text-align: left;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  color: #555;
}

.form-group input {
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  outline: none;
}

.form-group input:focus {
  border-color: #000;
}

.header-title {
  font-size: 1.5rem;
  color: #000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.pricing-plans {
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-top: 32px;
  flex-wrap: wrap;
  width: 100%;
}

.plan-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  max-width: 200px;
  flex: 1;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #f2f5f9;
}

.plan-card.selected {
  border: 2px solid #000;
}

.save-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  background: #4caf50;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
}

.price {
  font-size: 42px;
  margin: 0;
  color: #000;
  font-weight: 300;
}

.per-flight {
  color: #000;
  margin: 8px 0;
  font-size: 16px;
}

.subtitle {
  color: #888;
  margin: 4px 0 20px;
}

.credits {
  font-size: 18px;
  margin: 12px 0;
  color: black;
}

.price-breakdown {
  margin-top: 4rem;
  margin-bottom: 1rem;
  background-color: #f9fafb;
  padding: 2rem;
  border-radius: 12px;
  max-width: 400px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.breakdown-title {
  font-size: 1.25rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 1.5rem;
}

.breakdown-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  color: #000;
  font-size: 16px;
  padding: 0.5rem 0;
}

.breakdown-total {
  display: flex;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  color: #000;
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 2rem;
}

.payment-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.payment-btn {
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;
  cursor: pointer;
}

.stripe-btn {
  background-color: #4f46e5;
  border: none;
  color: #fff;
}

.stripe-btn:hover:not(:disabled) {
  background-color: #4338ca;
}

.payment-btn.disabled-btn {
  opacity: 0.5;
  cursor: not-allowed;
}

.payment-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

@media (max-width: 640px) {
  .price-breakdown {
    margin-top: 2rem;
    padding: 1.5rem;
    width: 100%;
  }

  .breakdown-title {
    font-size: 1.1rem;
  }

  .breakdown-row,
  .breakdown-total {
    font-size: 14px;
  }
}

.table-container {
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 500px; /* Adjust height as needed */
  background-color: #f5f5f5; /* Light gray background */
  border-radius: 8px;
  padding: 20px;
}

.empty-message {
  font-size: 1rem;
  color: #7e7e7e;
  text-align: center;
}

.credit-table {
  width: 100%;
  border: 1px solid #eee;
  border-radius: 12px;
  overflow: hidden;
}

.table-header {
  display: flex;
  background-color: #f9fafb;
  border-bottom: 1px solid #eee;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background-color: #f9fafb;
}

.th,
.td {
  flex: 0.7; /* Make all columns slightly smaller */
  padding: 16px;
  font-size: 14px;
}

.th {
  font-weight: 500;
  color: #6b7280;
}

.td {
  color: #111827;
}

/* Make description column wider */
.table-header > .th:nth-child(6), /* Description */
.table-body .td:nth-child(6) {
  flex: 2; /* Double width for description */
}

.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge.credit_added {
  background-color: #ecfdf5;
  color: #059669;
}

.badge.credit_spent {
  background-color: #fef2f2;
  color: #dc2626;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.pending {
  background-color: #fef3c7;
  color: #d97706;
}

.status-badge.completed {
  background-color: #ecfdf5;
  color: #059669;
}

.status-badge.cancelled {
  background-color: #fef2f2;
  color: #dc2626;
}

.status-badge.failed {
  background-color: #fef2f2;
  color: #dc2626;
}

.status-badge.refunded {
  background-color: #eff6ff;
  color: #3b82f6;
}

.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6b7280;
}

.credit-balance {
  font-weight: 600;
  color: #000;
  margin-left: 8px;
  font-size: 1.8rem;
}
</style>
